import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
} from "@gonoodle/gn-universe-ui";
import {
  stripQueryString,
  concatenateQueryParams,
} from "@gonoodle/gn-universe-utils";
import { decamelizeKeys } from "humps";
import { SECTIONS } from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { usePathname } from "next/navigation";

import { useLogEvent } from "../../contexts/Analytics";

function GoogleClassroomIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="currentColor"
      stroke="currentColor"
      className={className}
    >
      <g fill="#fff" fillRule="evenodd" clipRule="evenodd">
        <path d="M21.5 3h-18v18h9v-1.1h6.6V21h2.4V3zm-18-2h-2v22h22V1h-20zm8.916 10.848a1.42 1.42 0 001.417-1.424A1.42 1.42 0 0012.416 9a1.42 1.42 0 00-1.417 1.424 1.42 1.42 0 001.417 1.424zm-3.25 3.685v-1.34s.854-1.675 3.333-1.675 3.334 1.675 3.334 1.675v1.34H9.165z"></path>
        <path
          d="M7.85 12.677c.607 0 1.099-.495 1.099-1.104 0-.61-.492-1.104-1.099-1.104-.606 0-1.098.494-1.098 1.104 0 .61.492 1.104 1.098 1.104zm.234.519c.675 0 1.191.16 1.582.37-.35.339-.499.627-.499.627v1.34H5.5v-1.039s.663-1.298 2.584-1.298zm8.767-.52c.607 0 1.099-.494 1.099-1.103 0-.61-.492-1.104-1.099-1.104-.606 0-1.098.494-1.098 1.104 0 .61.492 1.104 1.098 1.104zm.065.52c-.675 0-1.191.16-1.582.37.35.339.499.627.499.627v1.34H19.5v-1.039s-.663-1.298-2.584-1.298z"
          opacity="0.5"
        />
      </g>
    </svg>
  );
}

export default function ShareToGoogleClassroomButton({
  id,
  title,
  contentType,
}) {
  const pathname = usePathname();

  const { logEvent } = useLogEvent({
    event: "Content Shared",
    properties: {
      id,
      title,
      contentType,
      sourceName: SECTIONS.GOOGLE_CLASSROOM_SHARE_BUTTON,
    },
    options: {
      includeReferrer: false,
      includeSourcePage: true,
      includeSourcePageType: true,
    },
  });

  const handleGoogleClassroomShare = React.useCallback(() => {
    const urlToShare = concatenateQueryParams(
      stripQueryString(
        `${process.env.NEXT_PUBLIC_WEB_URL}${pathname}?utmSource=GoogleClassroom`,
      ),
      decamelizeKeys({
        sourcePage: "share",
        sourcePageType: "share",
        sourceName: "share",
        sourceElement: "share",
        utmSource: "GoogleClassroom",
      }),
    );

    window.open(
      concatenateQueryParams("https://classroom.google.com/share", {
        url: urlToShare,
      }),
      "_blank",
    );

    logEvent();
  }, [pathname, logEvent]);

  return (
    <Button
      variant={BUTTON_VARIANTS.lightOutline}
      onPress={handleGoogleClassroomShare}
      size={BUTTON_SIZES.md}
    >
      <span className="inline-flex justify-center items-center font-bold">
        Google Classroom
        <GoogleClassroomIcon className="h-md ml-xs" />
      </span>
    </Button>
  );
}

ShareToGoogleClassroomButton.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  contentType: PropTypes.string.isRequired,
};
